import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { CylinderType, CreateCylinderTypeDto, UpdateCylinderTypeDto, RefrigerantCylinder, PurchaseCylinderDto, CylinderMovement, CylinderStatus, ClearCylinderCustodyDto } from '../models/cylinder.model';
import { apiBase } from './api-config';

@Injectable({ providedIn: 'root' })
export class CylinderService {
  private http = inject(HttpClient);
  private typesBase = `${apiBase}/cylinder-types`;
  private cylBase = `${apiBase}/cylinders`;

  // Cylinder types
  getAllTypes(): Observable<CylinderType[]> {
    return this.http.get<CylinderType[]>(this.typesBase);
  }

  createType(dto: CreateCylinderTypeDto): Observable<CylinderType> {
    return this.http.post<CylinderType>(this.typesBase, dto);
  }

  updateType(id: number, dto: UpdateCylinderTypeDto): Observable<CylinderType> {
    return this.http.put<CylinderType>(`${this.typesBase}/${id}`, dto);
  }

  deleteType(id: number): Observable<void> {
    return this.http.delete<void>(`${this.typesBase}/${id}`);
  }

  // Cylinders
  getAll(status?: CylinderStatus, typeId?: number, holderId?: number): Observable<RefrigerantCylinder[]> {
    let params = new HttpParams();
    if (status) params = params.set('status', status);
    if (typeId) params = params.set('typeId', typeId.toString());
    if (holderId) params = params.set('holderId', holderId.toString());
    return this.http.get<RefrigerantCylinder[]>(this.cylBase, { params });
  }

  getById(id: number): Observable<RefrigerantCylinder> {
    return this.http.get<RefrigerantCylinder>(`${this.cylBase}/${id}`);
  }

  purchase(dto: PurchaseCylinderDto): Observable<RefrigerantCylinder> {
    return this.http.post<RefrigerantCylinder>(`${this.cylBase}/purchase`, dto);
  }

  attachToWorkOrder(id: number, workOrderId: number, performedByPersonnelId: number): Observable<RefrigerantCylinder> {
    return this.http.post<RefrigerantCylinder>(`${this.cylBase}/${id}/attach-to-work-order`, { workOrderId, performedByPersonnelId });
  }

  release(id: number, performedByPersonnelId: number): Observable<RefrigerantCylinder> {
    return this.http.post<RefrigerantCylinder>(`${this.cylBase}/${id}/release`, { performedByPersonnelId });
  }

  decommission(id: number, performedByPersonnelId: number, notes?: string): Observable<RefrigerantCylinder> {
    return this.http.post<RefrigerantCylinder>(`${this.cylBase}/${id}/decommission`, { performedByPersonnelId, notes });
  }

  clearCustody(id: number, dto: ClearCylinderCustodyDto): Observable<RefrigerantCylinder> {
    return this.http.post<RefrigerantCylinder>(`${this.cylBase}/${id}/clear-custody`, dto);
  }

  getMovements(cylinderId: number): Observable<CylinderMovement[]> {
    return this.http.get<CylinderMovement[]>(`${this.cylBase}/${cylinderId}/movements`);
  }
}
